import { Box, Grow, styled, Typography } from "@mui/material";
import { Key, Lock, Person, Refresh } from "@mui/icons-material";
import { Form, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { registerUserCode, registerUserNumber, } from "../../lib/dashboard/actions";
import { signupOTPSchema, signupUsernameSchema } from "../../validation";
import { toast } from "react-toastify";
import AuthContainer from "./AuthContainer";
import { COLORS } from "../../ui/Theme";
import useResponsive from "../../components/hooks/useResponsive";
import AuthTextfieldLabel from "./AuthTextfieldLabel";
import AuthTextfield from "./AuthTextfield";
import Loading from "../../components/loader/Loading";

const CustomBox = styled("div")({
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
    width: "100%",
    gap: "6px",
});

const SignupForm = ({ url }) => {
    const { isSm } = useResponsive();
    const [isRegLoading, setIsRegLoading] = useState(false)

    const [info, setInfo] = useState({
        username: "",
        code: "",
        token: "",
        password: "",
        confirmPassword: "",
    });
    const [error, setError] = useState({
        usernameError: "",
        codeError: "",
        passwordError: "",
        confirmPasswordError: "",
    });
    const [cooldown, setCooldown] = useState(false);
    const [countdown, setCountdown] = useState(300);

    useEffect(() => {
        if (cooldown && countdown > 0) {
            const interval = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);

            return () => clearInterval(interval);
        } else if (countdown === 0) {
            setCooldown(false);
            setCountdown(300);
        }
    }, [cooldown, countdown]);

    const usernameHandler = async (e) => {
        setIsRegLoading(true)

        e.preventDefault();
        const validationUsername = signupUsernameSchema.safeParse({
            username: info.username,
        });

        if (!validationUsername.success) {
            setError({
                usernameError: "",
                codeError: "",
                passwordError: "",
            });
            setError((prev) => ({
                ...prev,
                usernameError: validationUsername.error.errors[0].message,
            }));
            return;
        }

        setError({
            usernameError: "",
            codeError: "",
            passwordError: "",
        });

        const formData = new FormData();
        formData.append("username", info.username);

        const res = await registerUserNumber(formData, url);

        if (res.msg === "ok") {
            toast.success(res.data.message);
            setInfo((prev) => ({ ...prev, token: res.data.token }));
            setCooldown(true);
            setCountdown(300);
        } else {
            toast.error(res.data.message);
        }
        setIsRegLoading(false)

    };

    const OTPHandler = async (e) => {

        e.preventDefault();
        const validationResult = signupOTPSchema.safeParse({
            username: info.username,
            code: info.code,
            password: info.password,
            confirmPassword: info.confirmPassword,
        });

        if (!validationResult.success) {
            setError({
                usernameError: "",
                codeError: "",
                passwordError: "",
                confirmPasswordError: "",
            });

            validationResult.error.errors.forEach((err) => {
                const field = `${err.path[0]}Error`;
                setError((prev) => ({ ...prev, [field]: err.message }));
            });

            return;
        }

        setError({
            usernameError: "",
            codeError: "",
            passwordError: "",
            confirmPasswordError: "",
        });

        const formData = new FormData();
        formData.append("username", info.username);
        formData.append("code", info.code);
        formData.append("token", info.token);
        formData.append("password", info.password);

        const res = await registerUserCode(formData, url);

        if (res.msg === "ok") {
            toast.success(res.data.message);
            window.location.href = "/login";
        } else {
            toast.error(res.data.message);
        }
    };

    const changeUsernameHandler = () => {
        setInfo({
            username: "",
            code: "",
            token: "",
            password: "",
            confirmPassword: "",
        });
    };

    const resendCodeHandler = (e) => {
        e.preventDefault();
        usernameHandler(e);
        setCooldown(true);
        setCountdown(300);
    };

    return (
        <AuthContainer>
            <Form
                onSubmit={info.token ? OTPHandler : usernameHandler}
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    backgroundColor: "#fff",
                    padding: isSm ? "40px" : "30px",
                    gap: 20,
                    border: "1px solid #EEEEEE",
                    borderRadius: "12px",
                    borderColor: "#EEEEEE",
                    minWidth: "390px",
                }}
            >
                <Typography
                    sx={{ color: COLORS.BLACK, fontSize: "20px", fontWeight: 400 }}
                >
                    به{" "}
                    <Typography
                        component="span"
                        sx={{
                            color: COLORS.BLUE,
                            fontSize: "20px",
                            fontWeight: 700,
                        }}
                    >
                        پرتفویار
                    </Typography>{" "}
                    خوش آمدید
                </Typography>

                {/*username (phone)*/}
                <CustomBox>
                    <AuthTextfieldLabel label="نام کاربری (شماره موبایل)" />
                    <AuthTextfield
                        value={info.username}
                        onChange={(e) =>
                            setInfo((prev) => ({ ...prev, username: e.target.value }))
                        }
                        type="text"
                        placeholder="شماره موبایل"
                        icon={<Person sx={{ color: "#BDBDBD" }} />}
                        error={!!error.usernameError}
                        helperText={error.usernameError}
                        disabled={!!info.token}
                    />
                </CustomBox>

                {/*password*/}
                {info.token && (
                    <>
                        <Grow in={!!info.token} timeout={500}>
                            <CustomBox>
                                <AuthTextfieldLabel label="رمز عبور" />
                                <AuthTextfield
                                    value={info.password}
                                    onChange={(e) =>
                                        setInfo((prev) => ({ ...prev, password: e.target.value }))
                                    }
                                    type="password"
                                    placeholder="رمز عبور"
                                    icon={<Lock sx={{ color: "#BDBDBD" }} />}
                                    error={!!error.passwordError}
                                />
                            </CustomBox>
                        </Grow>
                    </>
                )}

                {/*confirm password*/}
                {info.token && (
                    <>
                        <Grow in={!!info.token} timeout={500}>
                            <CustomBox>
                                <AuthTextfieldLabel label="تایید رمز عبور" />
                                <AuthTextfield
                                    value={info.confirmPassword}
                                    onChange={(e) =>
                                        setInfo((prev) => ({
                                            ...prev,
                                            confirmPassword: e.target.value,
                                        }))
                                    }
                                    type="password"
                                    placeholder="تایید رمز عبور"
                                    icon={<Lock sx={{ color: "#BDBDBD" }} />}
                                    error={!!error.confirmPasswordError}
                                    helperText={error.confirmPasswordError}
                                />
                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        color: error.passwordError ? "#D32F2F" : "#9F9CA2",
                                    }}
                                >
                                    رمز عبور باید شامل حداقل 8 کاراکتر، یک حرف کوچک، یک حرف بزرگ،
                                    یک عدد و یک کاراکتر خاص باشد
                                </Typography>
                            </CustomBox>
                        </Grow>
                    </>
                )}

                {/*confirimation code*/}
                {info.token && (
                    <>
                        <Grow in={!!info.token} timeout={500}>
                            <Box
                                sx={{
                                    display: "flex",
                                    position: "relative",
                                    gap: "10px",
                                    width: "100%",
                                }}
                            >
                                <CustomBox>
                                    <AuthTextfieldLabel label="کد ارسال شده" />
                                    <AuthTextfield
                                        value={info.code}
                                        onChange={(e) =>
                                            setInfo((prev) => ({ ...prev, code: e.target.value }))
                                        }
                                        type="number"
                                        placeholder="کد"
                                        icon={<Key sx={{ color: "#BDBDBD" }} />}
                                        error={!!error.codeError}
                                        helperText={error.codeError}
                                    />
                                </CustomBox>
                                <Typography
                                    onClick={resendCodeHandler}
                                    disabled={cooldown}
                                    sx={{
                                        position: "absolute",
                                        top: "0",
                                        right: "0",
                                        fontSize: "12px",
                                        fontWeight: 500,
                                        color: cooldown ? COLORS.GRAY : COLORS.BLUE,
                                        cursor: cooldown ? "default" : "pointer",
                                    }}
                                >
                                    {cooldown ? (
                                        `${Math.floor(countdown / 60)}:${String(
                                            countdown % 60
                                        ).padStart(2, "0")}`
                                    ) : (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                            }}
                                        >
                                            ارسال مجدد
                                            <Refresh
                                                sx={{
                                                    color: cooldown ? COLORS.GRAY : COLORS.BLUE,
                                                    fontSize: "18px",
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Typography>
                            </Box>
                        </Grow>
                    </>
                )}
                <Box
                    sx={{
                        display: "flex",
                        width: "100%",
                        gap: "10px",
                    }}
                >
                    <button className="btn btn-blue" type="submit" disabled={isRegLoading}>

                        {isRegLoading ? <Loading color={COLORS.WHITE} /> : !!info.token ? "ثبت" : "مرحله بعد"}
                    </button>
                    {info.token && (
                        <Grow in={!!info.token} timeout={500}>
                            <button
                                className={"btn btn-softblue"}
                                onClick={changeUsernameHandler}
                            >
                                تغییر شماره موبایل
                            </button>
                        </Grow>
                    )}
                </Box>
            </Form>
            <Typography fontSize={12}>
                <span>در پرتفویار اکانت دارید؟</span>{" "}
                <Link
                    to={"/login"}
                    style={{ fontSize: "14px", fontWeight: 900, color: COLORS.BLUE }}
                >
                    ورود
                </Link>
            </Typography>
        </AuthContainer>
    );
};

export default SignupForm;
